<template>
  <div class="exercise-definition-dialog-form-body">
    <v-container
      class="exercise-definition-dialog-card-container"
      v-if="parametersEnabled"
    >
      <v-tabs
        v-model="tab"
        fixed-tabs
        color="accent"
        @change="onInput({ name: 'time', value: timeValues[tab] })"
      >
        <v-tabs-slider />
        <v-tab>None</v-tab>
        <v-tab>AM</v-tab>
        <v-tab>PM</v-tab>
      </v-tabs>
    </v-container>
    <v-container class="exercise-definition-dialog-card-container">
      <dialog-input
        label="Title"
        name="title"
        :value="(editedExercise && editedExercise.title) || ''"
        :textarea="true"
        :readonly="readonly"
        :rules="['counter']"
        :textareaExpandable="true"
        @input="onInput"
      />
    </v-container>
    <v-container class="exercise-definition-dialog-card-container">
      <dialog-input
        label="Description"
        name="description"
        :readonly="readonly"
        :value="(editedExercise && editedExercise.description) || ''"
        :textarea="true"
        :rules="['counter']"
        :textareaExpandable="true"
        @input="onInput"
      />
    </v-container>
    <v-container class="exercise-definition-dialog-card-container mb-4">
      <dialog-input
        label="External URL"
        name="externalUrl"
        :value="(editedExercise && editedExercise.externalUrl) || ''"
        :readonly="readonly"
        :rules="['counter']"
        :textarea="true"
        :textareaExpandable="true"
        @input="onInput"
      />
    </v-container>
    <v-container v-if="repetitionOptionsEnabled">
      <div>
        <h5>Repeat</h5>
        <div class="d-flex align-center">
          <bh-number-input
            name="repetitionWeeks"
            v-model="repetitionWeeksValue"
            :value="0"
            :readonly="readonly"
            :minNumber="0"
            :maxNumber="20"
            input-class="rounded-0"
            @input="onInput({ name: 'repetitionWeeks', value: $event })"
          ></bh-number-input>
          <span class="ml-2">weeks</span>
        </div>
      </div>
      <div v-if="repetitionDaysOptions && repetitionDaysOptions.length > 0">
        <v-chip-group
          active-class="primary--text"
          v-model="repetitionDaysValue"
          :value="(editedExercise && editedExercise.repetitionDays) || []"
          multiple
          @change="onInput({ name: 'repetitionDays', value: $event })"
        >
          <v-chip
            v-for="day in repetitionDaysOptions"
            :key="day.value"
            :value="day.value"
            :readonly="readonly"
            active-class="exercise-definition-dialog__repetition-day--active"
            class="exercise-definition-dialog__repetition-day"
            name="repetitionDays"
          >
            {{ day.label }}
          </v-chip>
        </v-chip-group>
      </div>
    </v-container>
    <v-container
      class="exercise-definition-dialog-card-container pt-0"
      v-if="!readonlyMedia"
    >
      <v-row>
        <v-col sm="12" class="pt-0" v-if="!readonly">
          <thumbnail-picker
            label="Content"
            :current-file="newExerciseData.currentMedia"
            :readonly="readonlyMedia"
            @thumbnail-picked="setMedia"
          />
        </v-col>
      </v-row>
    </v-container>
    <v-container
      :class="
        `exercise-definition-dialog-card-container ${
          showAdditionalMedia ? '' : 'd-none'
        }`
      "
    >
      <v-row>
        <v-col sm="12" class="pt-0" v-if="!readonly">
          <thumbnail-picker
            label="Thumbnail"
            :current-file="newExerciseData.currentAdditionalMedia"
            :current-mobile-static-media="
              newExerciseData.currentMobileStaticMedia
            "
            :enable-mobile-static-media="true"
            :force-mobile-static-media="true"
            :readonly="readonly"
            :allowed-file-types="[18]"
            @thumbnail-picked="setAdditionalMedia"
          />
        </v-col>
      </v-row>
    </v-container>
    <v-container
      class="exercise-definition-dialog-card-container"
      v-if="showAddToCollection"
    >
      <v-row>
        <v-col sm="12">
          <v-switch
            class="v-input--reverse"
            color="accent"
            v-model="addToCollection"
            :readonly="readonly"
          >
            <template #label>
              <v-list-item-subtitle>
                Add to collection
              </v-list-item-subtitle>
            </template>
          </v-switch>
        </v-col>
      </v-row>
    </v-container>
    <v-container
      class="d-flex justify-space-between align-center exercise-definition-dialog__tab--footer"
    >
      <v-btn
        v-if="!hidePrev"
        rounded
        color="primary"
        elevation="0"
        @click="prevStepTask"
        :disabled="!cancelable"
      >
        Prev
      </v-btn>
      <span v-else></span>
      <v-spacer />
      <v-btn
        rounded
        color="primary"
        elevation="0"
        :loading="submitLoading"
        @click="onSubmit"
        :disabled="!formIsValidCheck"
      >
        {{ editedExercise ? 'Save' : 'Create' }}
      </v-btn>
    </v-container>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import DialogInput from '@/components/forms/DialogInput.vue'
import BhNumberInput from '@/components/inputs/BhNumberInput.vue'
import RouterMixin from '@/mixins/router'
import TenantTextsMixin from '@/mixins/tenantTexts'
import { REPETITION_DAYS_OPTIONS } from '@/helpers/constants'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'

const emptyForm = {}

export default {
  name: 'TaskExerciseDefinitionPartial',
  components: {
    BhNumberInput,
    DialogInput,
    ThumbnailPicker,
  },
  mixins: [RouterMixin, TenantTextsMixin],
  props: {
    getResource: {
      type: Function,
      default: () => null,
    },
    exerciseResource: {
      type: [Boolean, Object, null],
      default: false,
    },
    exerciseResourceAdditional: {
      type: [Boolean, Object, null],
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    day: {
      type: [Boolean, Number],
      default: false,
    },
    getMimeTypes: {
      type: Function,
      default: () => null,
    },
    editedExercise: {
      type: [Boolean, Object],
      default: false,
    },
    exerciseDefinitionsLoading: {
      type: Boolean,
      default: false,
    },
    cancelable: {
      type: Boolean,
      default: false,
    },
    parametersEnabled: {
      type: Boolean,
      default: false,
    },
    hidePrev: {
      type: Boolean,
      default: false,
    },
    showAddToCollection: {
      type: Boolean,
      default: false,
    },
    readonlyMedia: {
      type: Boolean,
      default: false,
    },
    showAdditionalMedia: {
      type: Boolean,
      default: val => (val !== false ? true : false),
    },
    repetitionOptionsEnabled: {
      type: Boolean,
      default: false,
    },
    waitForSubmitResponse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tab: 0,
      newExerciseData: {
        title: '',
        description: '',
        externalUrl: '',
        currentMedia: null,
        media: null,
        currentAdditionalMedia: null,
        additionalMedia: null,
        repetitionWeeks: 0,
        repetitionDays: [],
        mobileStaticMedia: null,
        time: null,
      },
      timeValues: [null, 'MORNING', 'EVENING'],
      repetitionWeeksValue: 0,
      repetitionDaysValue: [],
      showMlFilePickerDialog: false,
      showMlFilePickerDialogType: 'media',
      resourceName: null,
      resourceNameAdditional: null,
      formIsValidCheck: false,
      showStaticMediaPicker: false,
      addToCollection: true,
      submitLoading: false,
    }
  },
  watch: {
    editedExercise: function(val) {
      this.submitLoading = false
      this.setupExerciseData()
    },
  },
  created() {
    this.submitLoading = false
    this.setupExerciseData()
  },
  computed: {
    ...mapState('staticMediaStates', ['staticMedia']),
    repetitionDaysOptions() {
      if (!REPETITION_DAYS_OPTIONS) return []
      return Object.values(REPETITION_DAYS_OPTIONS).map(value => ({
        label: value.substr(0, 1),
        value,
      }))
    },
  },
  methods: {
    ...mapMutations('appStates', ['setSnackbarNotification']),
    placeholder(fileTypeId) {
      let image = 'placeholder_other.png'
      switch (fileTypeId) {
        case 17:
          image = 'placeholder_other.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
    formIsValid() {
      this.formIsValidCheck = true
      if (
        this.newExerciseData.title.trim() == '' ||
        this.newExerciseData.title.length > 250
      )
        this.formIsValidCheck = false
      if (this.newExerciseData.description.length > 250)
        this.formIsValidCheck = false
      if (this.newExerciseData.externalUrl.length > 250)
        this.formIsValidCheck = false
    },
    closeDialog() {
      this.$emit('close-exercise-dialog')
      this.$emit('clear-resource')
    },
    onInput({ name, value }) {
      this.newExerciseData[name] = value
      this.formIsValid()
    },
    onSubmit() {
      this.newExerciseData.type = 'ACTIVITY'

      if (this.day) this.newExerciseData.day = this.day

      if (this.repetitionOptionsEnabled) {
        this.newExerciseData.repetitionWeeks = this.repetitionWeeksValue
        this.newExerciseData.repetitionDays = this.repetitionDaysValue
      }

      if (this.editedExercise) {
        this.submitLoading = true
        this.$emit('on-edit', { ...this.newExerciseData }, this.addToCollection)
      } else {
        this.submitLoading = true
        this.$emit(
          'on-submit',
          { ...this.newExerciseData },
          this.addToCollection,
        )
      }
      if (!this.waitForSubmitResponse) {
        this.closeDialog()
      }
    },
    prevStepTask() {
      if (this.cancelable) this.$emit('cancel')
    },
    setupExerciseData() {
      if (
        !this.newExerciseData.media &&
        !this.newExerciseData.currentMedia &&
        this.exerciseResource?.id
      ) {
        this.newExerciseData.currentMedia = {
          ...this.exerciseResource,
          mlSourceResourceId: this.exerciseResource.id,
        }
        this.newExerciseData.media = {
          ...this.exerciseResource,
          mlSourceResourceId: this.exerciseResource.id,
        }
      }

      if (
        !this.newExerciseData.additionalMedia &&
        !this.newExerciseData.currentAdditionalMedia &&
        this.exerciseResourceAdditional?.id
      ) {
        this.newExerciseData.currentAdditionalMedia = {
          ...this.exerciseResourceAdditional,
          mlSourceResourceId: this.exerciseResourceAdditional.id,
        }
        this.newExerciseData.additionalMedia = {
          ...this.exerciseResourceAdditional,
          mlSourceResourceId: this.exerciseResourceAdditional.id,
        }
      }

      if (this.editedExercise) {
        this.newExerciseData = { ...this.editedExercise }

        if (!this.newExerciseData.mobileStaticMedia) {
          this.newExerciseData.currentAdditionalMedia = null
          this.newExerciseData.mobileStaticMedia = null
        } else {
          this.newExerciseData.currentMobileStaticMedia = this.newExerciseData
            .mobileStaticMedia?.mediaUrl
            ? {
                thumbnailUrl: this.newExerciseData.mobileStaticMedia?.mediaUrl,
                mediaUrl: this.newExerciseData.mobileStaticMedia?.mediaUrl,
              }
            : null
        }

        if (!this.newExerciseData.media) {
          this.newExerciseData.media = null
          this.newExerciseData.currentMedia = null
        } else {
          this.newExerciseData.currentMedia =
            this.newExerciseData?.media?.mediaUrl ||
            this.newExerciseData?.media?.fileUrl ||
            this.newExerciseData?.media?.previewUrl ||
            this.newExerciseData?.media?.thumbnailUrl
              ? {
                  fileTypeId: this.newExerciseData?.media?.fileTypeId,
                  mediaUrl:
                    this.newExerciseData?.media?.mediaUrl ||
                    this.newExerciseData?.media?.fileUrl ||
                    this.newExerciseData?.media?.previewUrl ||
                    null,
                  thumbnailUrl: this.newExerciseData?.media?.thumbnailUrl,
                }
              : null
        }

        if (!this.newExerciseData.additionalMedia) {
          this.newExerciseData.currentAdditionalMedia = null
          this.newExerciseData.additionalMedia = null
        } else {
          this.newExerciseData.currentAdditionalMedia = this.newExerciseData
            ?.additionalMedia?.thumbnailUrl
            ? {
                mediaUrl:
                  this.newExerciseData?.additionalMedia?.mediaUrl ||
                  this.newExerciseData?.additionalMedia?.fileUrl ||
                  this.newExerciseData?.additionalMedia?.previewUrl ||
                  null,
                thumbnailUrl: this.newExerciseData?.additionalMedia
                  ?.thumbnailUrl,
              }
            : null
        }

        if (this.repetitionOptionsEnabled) {
          if (!this.newExerciseData.repetitionWeeks)
            this.newExerciseData.repetitionWeeks = 0
          if (!this.newExerciseData.repetitionDays)
            this.newExerciseData.repetitionDays = []

          this.repetitionWeeksValue = this.newExerciseData.repetitionWeeks
          this.repetitionDaysValue = this.newExerciseData.repetitionDays
        }

        if (this.newExerciseData.time)
          this.tab = this.timeValues.findIndex(
            item => item == this.newExerciseData.time,
          )
      }
      this.formIsValid()
    },
    setMedia(thumbnail = null) {
      this.setExerciseFile('media', thumbnail)
    },
    setAdditionalMedia(thumbnail = null) {
      this.setExerciseFile('additionalMedia', thumbnail)
    },
    setExerciseFile(exerciseFileType, thumbnail = null) {
      const availableTypes = ['media', 'additionalMedia']

      if (!availableTypes.includes(exerciseFileType)) {
        return
      }

      if (thumbnail === null) {
        // remove
        this.newExerciseData[exerciseFileType] = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.newExerciseData[exerciseFileType] = {
            mlSourceResourceId: file.id,
          }
        } else if (type === 'file-picker') {
          this.newExerciseData[exerciseFileType] = file
        } else if (type === 'mobile-static-media') {
          this.newExerciseData.mobileStaticMedia = file
          this.newExerciseData[exerciseFileType] = { remove: true }
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.exercise-definition-dialog {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &__toolbar {
    flex-grow: 0;
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &__tabs {
    flex-grow: 0;
  }
  &__tabs-items {
    background-color: $bh-blue--4;
    flex-grow: 1;
    & > .v-window__container {
      height: 100%;
      flex-grow: 1;
    }
  }
  &__tab {
    height: 100%;
    background-color: $bh-blue--4;
    display: flex;
    flex-direction: column;
    & > .v-tabs-items {
      overflow: auto;
      flex-grow: 1;
      background-color: $bh-blue--4;
    }
    .v-input--switch {
      margin: 0;
    }
    .v-label {
      font-size: 12px;
      color: $bh-gray--2;
    }
  }
  &__dots {
    span {
      width: 16px;
      height: 16px;
      display: inline-block;
      border-radius: 50%;
      background-color: $bh-white;
      margin: 0 3px;
      transition-duration: 300ms;
      &.active {
        background-color: $bh-blue;
      }
    }
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-file-button {
    margin-right: 10px;
    .v-btn {
      cursor: pointer;
    }
    & > label {
      cursor: pointer;
    }
    label {
    }
    .v-btn::before {
      display: none;
    }
    .v-icon {
      &:first-child {
        padding-right: 10px;
      }
      &:last-child {
        padding-left: 10px;
      }
    }
  }
  &-file-details {
    display: inline-block;
    vertical-align: middle;
    flex-grow: 1;
    img {
      width: 40px;
      height: 40px;
      display: inline-block;
      vertical-align: middle;
      object-fit: cover;
      margin-right: 10px;
      cursor: pointer;
    }
    .v-progress-circular {
      margin-right: 10px;
    }
    .v-icon {
      margin-left: 5px;
      padding: 4px;
    }
    span {
      cursor: default;
      white-space: nowrap;
      max-width: calc(100% - 80px);
      display: inline-block;
      vertical-align: middle;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      height: 100%;
      display: flex;
      flex-direction: column;
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
      & > .v-tabs-items {
        flex-grow: 1;
        background-color: $bh-blue--4;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }

  &__repetition-day {
    font-weight: 600;
    background-color: $bh-white !important;
    color: $bh-blue !important;
    border: 1px solid $bh-blue !important;

    &--active {
      background-color: $bh-blue !important;
      color: $bh-white !important;
    }
  }
}

.add-media-menu__list-item .v-btn {
  width: 100%;
  justify-content: flex-start;

  .v-icon {
    width: 16px;
  }
}

.exercise-definition-dialog-form-body {
  .file-uploads {
    vertical-align: middle;
    overflow: auto !important;
    width: 100%;

    .v-btn {
      width: 100%;
    }

    &.file-uploads-html5 {
      &:not(.disabled) label:hover {
        opacity: 0.08;
      }
      &:not(.disabled) label {
        cursor: pointer;
      }
      label {
        background-color: $bh-blue !important;
        transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
        pointer-events: none;
        opacity: 0;
      }
    }
    &.disabled {
      pointer-events: none;
      .v-btn {
        width: 100%;
        background-color: rgba(0, 0, 0, 0.12) !important;
        color: rgba(0, 0, 0, 0.26) !important;
      }
    }
  }
}
</style>
